import * as React from 'react';
import DialogBackdrop from 'components/dialog/dialogBackdrop';
import styled from 'styled-components';
import { Form, Field, H1, Button } from 'components/element';
import { useTranslation } from 'react-i18next';
import color from 'styles/color';
import CustomerInput from 'components/transaction/CustomerInput';
import useBookingForm from 'hooks/useBookingForm';
import OutletServiceInput from 'components/transaction/OutletServiceInput';
import OutletProductsInput from 'components/transaction/OutletProductsInput';
import OutletStylistWithTimeInput, {
  FormField,
} from 'components/booking/OutletStylistWithTimeInput';
import { FormikResult } from '../../../../typings/formik';
import { OutletStylist } from 'repositories/outlet';
import { useNetworkStatus } from 'containers/NetworkStatusContainer';
import { Stylist } from 'repositories/stylist';

interface Props {
  onClose: () => void;
  date: Date;
}

export default function AddBooking(props: Props) {
  const { t } = useTranslation();
  const { mode } = useNetworkStatus();
  const [stylist, setStylist] = React.useState<OutletStylist | undefined>(
    undefined,
  );

  const { onClose } = props;

  const { formik, isLoading, bookingTypes, setIsLoading } = useBookingForm({
    date: props.date,
    onSuccess() {
      onClose();
    },
  });

  const onChangeStylist = React.useCallback(
    (stylist) => {
      formik.setFieldValue('stylist', stylist?.id);

      if (mode) {
        setStylist(stylist);
        return;
      }

      // Update stylist's services
      const selectedStylist = {
        ...stylist,
        service: (stylist as Stylist).outletServiceStylists
          ?.filter(({ outletServiceId }) =>
            formik.values.services.some(({ id }) => id === outletServiceId),
          )
          ?.map(({ outletServiceId, ...service }) => ({
            ...service,
            id: outletServiceId,
          })),
      };

      setStylist(selectedStylist);
    },
    [mode, formik],
  );

  const onChangeService = React.useCallback(
    (services) => {
      if (!mode && !!stylist) {
        // Update price and point based on selected stylist
        const selectedServices = services.map((service) => {
          const foundStylist = service.service.stylists?.find(
            (s) => s.id === stylist.id,
          );
          const originalPrice =
            service.service.originalPrice ?? service.service.price;
          const originalPoint =
            service.service.originalPoint ?? service.service.point;
          const originalCost =
            service.service.originalCost ?? service.service.cost;

          if (typeof foundStylist !== 'undefined') {
            return {
              ...service,
              point: foundStylist.point,
              price: foundStylist.price,
              cost: foundStylist.cost,
              totalPrice: foundStylist.price,
              service: {
                ...service.service,
                price: foundStylist.price,
                point: foundStylist.point,
                cost: foundStylist.cost,
                originalPrice,
                originalPoint,
                originalCost,
              },
            };
          }
          return {
            ...service,
            point: originalPoint,
            price: originalPrice,
            cost: originalCost,
            totalPrice: originalPrice,
            service: {
              ...service.service,
              price: originalPrice,
              point: originalPoint,
              cost: originalCost,
              originalPrice,
              originalPoint,
              originalCost,
            },
          };
        });

        formik.setFieldValue('services', selectedServices);

        setStylist({
          ...stylist,
          service: selectedServices,
        });

        return;
      }

      formik.setFieldValue('services', services);
    },
    [mode, stylist],
  );

  React.useEffect(() => {
    if (!stylist || mode) return;

    const stylistPriceMap = new Map(
      stylist.service?.map(({ id, price, point, cost }) => [
        id,
        { price, point, cost },
      ]),
    );

    const updatedServices = formik.values.services.map((service) => {
      const pricing = stylistPriceMap.get(service.id);
      const originalPrice =
        (service.service as any).originalPrice ?? service.service.price;
      const originalPoint =
        (service.service as any).originalPoint ?? service.service.point;
      const originalCost =
        (service.service as any).originalCost ?? service.service.cost;

      if (typeof pricing === 'undefined') {
        return {
          ...service,
          price: originalPrice,
          point: originalPoint,
          cost: originalCost,
          totalPrice: originalPrice,
          service: {
            ...service.service,
            price: originalPrice,
            point: originalPoint,
            cost: originalCost,
            originalPrice,
            originalPoint,
            originalCost,
          },
        };
      }

      return {
        ...service,
        price: pricing?.price,
        point: pricing?.point,
        cost: pricing?.cost,
        totalPrice: pricing?.price,
        service: {
          ...service.service,
          price: pricing?.price,
          point: pricing?.point,
          cost: pricing?.cost,
          originalPrice,
          originalPoint,
          originalCost,
        },
      };
    });

    // Prevent unnecessary Formik updates
    const changed = updatedServices.some(
      (service, index) => service.price !== formik.values.services[index].price,
    );

    if (changed) {
      formik.setFieldValue('services', updatedServices);
    }
  }, [stylist, formik.values.services, mode]);

  return (
    <DialogBackdrop onClose={onClose} cancelable={false}>
      <Panel>
        <Form formik={formik}>
          <Title>{t('common.new_booking')}</Title>
          <BookingContent>
            <CustomerInput
              formik={formik}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
            <Field
              isSearchable={false}
              type="select"
              name="type"
              options={bookingTypes}
            />
            <OutletServiceInput
              selectedStylist={stylist}
              values={formik.values.services}
              onChange={onChangeService}
            />
            {/* <OutletProductsInput
              values={formik.values.products}
              onChange={(products) =>
                formik.setFieldValue('products', products)
              }
            /> */}
            <OutletStylistWithTimeInput
              formik={(formik as unknown) as FormikResult<FormField>}
              date={props.date}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              onChange={onChangeStylist}
            />
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={!formik.isValid}
            >
              {t('booking.add_booking')}
            </Button>
          </BookingContent>
        </Form>
        <CloseButton onClick={onClose}>x</CloseButton>
      </Panel>
    </DialogBackdrop>
  );
}

const Title = styled(H1)`
  text-align: center;
  margin-bottom: 1.5rem;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${color.white};
  color: ${color.black};
  font-size: 36px;
  width: 40px;
  line-height: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
  outline: none;
  border: none;
  appearance: none;
`;

export const Panel = styled.div`
  min-width: 28%;
  background-color: white;
  border-radius: 5px;
  min-height: 300px;
  max-height: 90vh;
  position: relative;
  padding: 1rem;
  overflow-y: scroll;

  @media (max-width: 1023px) {
    width: 70%;
  }
`;

const BookingContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;
